import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { addPaymentRecord, dismissPaywall, clearOverdueTasks } from '@/utils/paywallHelpers';
import { loadTodos, saveTodos } from '@/utils/todoHelpers';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Process the successful payment
    const pendingPayment = localStorage.getItem('pendingPayment');
    
    if (pendingPayment) {
      try {
        const payment = JSON.parse(pendingPayment);
        
        // Record the payment with the actual Stripe session ID
        const { amount, type = 'daily' } = payment;
        
        // First: Convert overdue tasks to overdrive (if applicable) - BEFORE dismissing paywall
        if (type === 'clearOverdue') {
          const todos = loadTodos('dailyTodos');
          const updatedTodos = clearOverdueTasks(todos);
          saveTodos('dailyTodos', updatedTodos);
        }
        
        // Then: Record the payment
        addPaymentRecord(amount, sessionId || payment.sessionId, type);
        
        // Finally: Dismiss the paywall (only after all data changes are complete)
        dismissPaywall();
        
        // Clear pending payment
        localStorage.removeItem('pendingPayment');
        
        // Force cross-tab state synchronization
        setTimeout(() => {
          // Dispatch custom event for current tab
          window.dispatchEvent(new Event('paywallStateChanged'));
          
          // Trigger storage event for other tabs by modifying localStorage
          const timestamp = Date.now().toString();
          localStorage.setItem('paywallSyncTrigger', timestamp);
          localStorage.removeItem('paywallSyncTrigger');
        }, 100);
        
        toast({
          title: "Payment Successful!",
          description: payment.type === 'clearOverdue'
            ? "Paywall dismissed! You can now continue using the app."
            : `Thank you for your $${payment.amount} donation!`,
        });
      } catch (error) {
        console.error('PaymentSuccess: Error processing payment success:', error);
      }
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl">Payment Successful!</CardTitle>
          <CardDescription>
            Thank you for supporting our todo app. Your payment has been processed successfully.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button asChild className="w-full">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to App
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}